
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Home, PiggyBank, Eye } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";
import { PropertyPrice } from "./investment/PropertyPrice";
import { InvestmentDialog } from "./investment/InvestmentDialog";
import { usePropertyInvestment } from "./investment/usePropertyInvestment";

interface PropertyInvestmentOptionsProps {
  property: Tables<"real_estate_properties">;
  showInvestmentOptions?: boolean;
}

export const PropertyInvestmentOptions = ({ 
  property, 
  showInvestmentOptions = true 
}: PropertyInvestmentOptionsProps) => {
  const navigate = useNavigate();
  const { 
    showInvestDialog, 
    setShowInvestDialog, 
    isProcessing, 
    handleInvestment 
  } = usePropertyInvestment(property);

  if (!showInvestmentOptions) {
    return (
      <>
        <PropertyPrice property={property} />
        <Button 
          variant="outline"
          onClick={() => navigate(`/real-estate/${property.id}`)}
          className="shadow-sm hover:shadow-md transition-all w-full"
        >
          <Eye className="mr-2 h-4 w-4" />
          View Property Details
        </Button>
      </>
    );
  }

  return (
    <>
      <PropertyPrice property={property} />
      <div className="grid grid-cols-1 gap-2">
        <Button
          variant="default"
          onClick={() => handleInvestment('full', property.price.toString())}
          disabled={isProcessing}
          className="w-full shadow-sm hover:shadow-md transition-all flex items-center gap-2 bg-green-600 hover:bg-green-700"
        >
          <Home className="h-4 w-4" />
          Buy This Property
        </Button>
        
        <Button 
          variant="outline"
          onClick={() => navigate(`/real-estate/${property.id}`)}
          className="shadow-sm hover:shadow-md transition-all"
        >
          <Eye className="mr-2 h-4 w-4" />
          View Details
        </Button>
        
        <Button
          variant="outline"
          onClick={() => setShowInvestDialog(true)}
          disabled={isProcessing}
          className="shadow-sm hover:shadow-md transition-all text-purple-700 border-purple-200"
        >
          <PiggyBank className="h-4 w-4 mr-2" />
          Financing Options
        </Button>
      </div>

      <InvestmentDialog
        property={property}
        showDialog={showInvestDialog}
        setShowDialog={setShowInvestDialog}
        isProcessing={isProcessing}
        onInvest={handleInvestment}
      />
    </>
  );
};
